/* eslint-disable import/extensions */
import moment from 'moment';
import { timeZoneSpecificationOptions } from 'presentation/constant/RadioOptions/StaticRadioOptions';
import { convertDateToLocal } from 'presentation/utils/timeUtil';
import React, { useEffect, useState, useRef, useImperativeHandle } from 'react';
import { DynamicProp } from 'veronica-ui-component/dist/common/commonInterface';
import { HPHBadge, HPHGroupRadioButton, IconButton, Lozenges } from 'veronica-ui-component/dist/component/core';
import { HPHRed } from 'veronica-ui-component/dist/component/core/Colors';
import { DateTimePickerGlobalStyles, StyledCalendar, StyledInputErrorMsg, StyledInputLabel, StyledInputWrapper, StyledOptional, StyledRequired } from 'veronica-ui-component/dist/component/core/styled/dateTimePicker2.styled';

export interface DateTimePickerProps2 extends DynamicProp {
    id?: string;
    label?: string;
    disabled?: boolean;
    required?: boolean;
    optional?: boolean;
    helpIcon?: boolean;
    errorMessage?: string;
    systemIndicator?: boolean;
    toolTipText?: string;
    placeholder?: string;
    width?: string;
    showOnFocus?: boolean;
    value?: string | Date;
    onChange?: (e: Date | Date[] | undefined) => void;
}

const formatDate = (date: Date): string => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
};

const parseDateInput = (input: string): Date | null => {
    const trimmed = input.trim();
    const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s(\d{1,2}):(\d{1,2})$/;
    const match = trimmed.match(regex);
    if (!match) return null;

    const [, dayStr, monthStr, yearStr, hoursStr, minutesStr] = match;
    const day = Number(dayStr);
    const month = Number(monthStr);
    const year = Number(yearStr);
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    // Basic range checks
    if (
        day < 1 || day > 31 ||
        month < 1 || month > 12 ||
        hours > 23 ||
        minutes > 59
    ) return null;

    // Check valid day in month
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) return null;

    const date = new Date(year, month - 1, day, hours, minutes);
    return isNaN(date.getTime()) ? null : date;
};

// export interface NbisDateTimePickerRef {
//     getSelectedAmbiguousDate: (currentDate?: Date) => Date | null | undefined;
// }

const NbisDateTimePicker = React.forwardRef((props: DateTimePickerProps2, ref?: React.Ref<any>) => {
    const {
        id = 'DateTimePicker2',
        label = 'Label',
        disabled = false,
        required = false,
        optional = false,
        helpIcon = false,
        errorMessage = '',
        systemIndicator = false,
        toolTipText = 'Date time format dd/mm/yyyy hh:mm',
        value,
        placeholder = 'dd/mm/yyyy hh:mm',
        width = '250px',
        onChange,
        showOnFocus = true,
        ...rest
    } = props;
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
    const [inputValue, setInputValue] = useState('');
    const [inputError, setInputError] = useState('');
    const [hours, setHours] = useState('00');
    const [minutes, setMinutes] = useState('00');
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    const wrapperRef = useRef<HTMLDivElement>(null);
    const [radioValue, setRadioValue] = useState('');
    const [ambiguousDateTime, setAmbiguousDateTime] = useState<Map<string, Date>>(new Map());
    // const selectedAmbiguousDateRef = useRef<Date | null | undefined>(null);

    useEffect(() => {
        if (!value) {
            setSelectedDate(undefined);
            setInputValue('');
            setHours('00');
            setMinutes('00');
            setInputError('');
            handleChangeAmbiguousDateTime(undefined);
            return;
        }

        const date = typeof value === 'string' ? parseDateInput(value) || new Date(value) : value;
        if (date && !isNaN(date.getTime())) {
            setSelectedDate(date);
            setInputValue(formatDate(date));
            setCurrentMonth(date.getMonth());
            setCurrentYear(date.getFullYear());
            setHours(String(date.getHours()).padStart(2, '0'));
            setMinutes(String(date.getMinutes()).padStart(2, '0'));
            setInputError('');
        }
    }, [value]);

    // Sync selectedDate → input & time picker
    useEffect(() => {
        if (selectedDate) {
            setInputValue(formatDate(selectedDate));
            setCurrentMonth(selectedDate.getMonth());
            setCurrentYear(selectedDate.getFullYear());
            const newHours = String(selectedDate.getHours()).padStart(2, '0');
            const newMinutes = String(selectedDate.getMinutes()).padStart(2, '0');
            if (newHours !== hours) setHours(newHours);
            if (newMinutes !== minutes) setMinutes(newMinutes);
            handleChangeAmbiguousDateTime(selectedDate);
            setInputError('');
        } else {
            handleChangeAmbiguousDateTime(undefined);
        }
    }, [selectedDate]);

    // Click outside → close calendar
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        setInputError('');
    };

    const handleInputBlur = () => {
        const trimmed = inputValue.trim();

        if (!trimmed) {
            setSelectedDate(undefined);
            setHours('00');
            setMinutes('00');
            setInputError('');
            onChange?.(undefined);
            return;
        }

        const parsed = parseDateInput(trimmed);
        if (parsed) {
            setSelectedDate(parsed);
            setInputValue(formatDate(parsed));
            setHours(String(parsed.getHours()).padStart(2, '0'));
            setMinutes(String(parsed.getMinutes()).padStart(2, '0'));
            setInputError('');
            onChange?.(parsed);
        } else {
            setSelectedDate(undefined);
            setHours('00');
            setMinutes('00');
            setInputError('Invalid format. Use dd/mm/yyyy hh:mm');
            onChange?.(undefined);
        }
    };

    const getDaysInMonth = (month: number, year: number) => {
        const first = new Date(year, month, 1);
        const days: (number | null)[] = [];
        for (let i = 0; i < first.getDay(); i++) days.push(null);
        for (let i = 1; i <= new Date(year, month + 1, 0).getDate(); i++) days.push(i);
        return days;
    };

    const days = getDaysInMonth(currentMonth, currentYear);
    const today = new Date();

    const selectDay = (day: number) => {
        const newDate = new Date(
            currentYear,
            currentMonth,
            day,
            selectedDate ? selectedDate.getHours() : 0,
            selectedDate ? selectedDate.getMinutes() : 0,
        );
        setSelectedDate(newDate);
        onChange?.(newDate);
    };

    const prevMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(y => y - 1);
        } else {
            setCurrentMonth(m => m - 1);
        }
    };

    const nextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(y => y + 1);
        } else {
            setCurrentMonth(m => m + 1);
        }
    };

    const changeTime = (
        type: 'hours' | 'minutes',
        dir: 'up' | 'down',
        e?: React.MouseEvent | React.TouchEvent | React.WheelEvent,
    ) => {
        if (e) e.preventDefault();
        if (type === 'hours') {
            const h = (parseInt(hours || '0') + (dir === 'up' ? 1 : -1) + 24) % 24;
            const newH = String(h).padStart(2, '0');
            setHours(newH);
            const nd = selectedDate ? new Date(selectedDate) : new Date();
            nd.setHours(h);
            setSelectedDate(nd);
            onChange?.(nd);
        } else {
            const m = (parseInt(minutes || '0') + (dir === 'up' ? 1 : -1) + 60) % 60;
            const newM = String(m).padStart(2, '0');
            setMinutes(newM);
            const nd = selectedDate ? new Date(selectedDate) : new Date();
            nd.setMinutes(m);
            setSelectedDate(nd);
            onChange?.(nd);
        }
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'hours' | 'minutes') => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 2);
        if (type === 'hours') setHours(val);
        else setMinutes(val);
    };

    const handleTimeBlur = (type: 'hours' | 'minutes') => {
        if (type === 'hours') {
            const h = parseInt(hours) || 0;
            const valid = h > 23 ? '00' : String(h).padStart(2, '0');
            setHours(valid);
            if (selectedDate) {
                const nd = new Date(selectedDate);
                nd.setHours(parseInt(valid));
                setSelectedDate(nd);
                onChange?.(nd);
            }
        } else {
            const m = parseInt(minutes) || 0;
            const valid = m > 59 ? '00' : String(m).padStart(2, '0');
            setMinutes(valid);
            if (selectedDate) {
                const nd = new Date(selectedDate);
                nd.setMinutes(parseInt(valid));
                setSelectedDate(nd);
                onChange?.(nd);
            }
        }
    };

    const handleRadioChange = (e: any) => {
        if (e.checked) {
            setRadioValue(e.key);
            if (ambiguousDateTime && ambiguousDateTime.size > 0) {
                const selectedAmbiguousDate = ambiguousDateTime.get(e.key);
                // selectedAmbiguousDateRef.current = selectedAmbiguousDate;
                props.onChange?.(selectedAmbiguousDate);
            }
        }
    }

    const monthNames = Array.from({ length: 12 }, (_, i) =>
        new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(2025, i, 1)),
    );

    const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log("systemTimeZone: " + systemTimeZone);
    const handleChangeAmbiguousDateTime = (date: Date | undefined) => {
        if (!date || isNaN(date.getTime())) {
            setAmbiguousDateTime(new Map());
            setRadioValue('');
            // selectedAmbiguousDateRef.current = undefined;
            return;
        }

        const result = checkIfAmbiguousTime(date);
        if (result) {
            const selectedRadioVal = radioValue || 'SUM';
            setRadioValue(selectedRadioVal);
            setAmbiguousDateTime(prevMap => {
                const newMap = new Map(prevMap);

                newMap.set("SUM", result.dstTime);
                newMap.set("REG", result.standardTime);

                return newMap;
            });
            // selectedAmbiguousDateRef.current = (selectedRadioVal === 'SUM' ? result.dstTime : result.standardTime);
            props.onChange?.((selectedRadioVal === 'SUM' ? result.dstTime : result.standardTime));
        } else {
            setAmbiguousDateTime(new Map());
            setRadioValue('');
            // selectedAmbiguousDateRef.current = undefined;
        }
    }

    const checkIfAmbiguousTime = (date: Date) => {
        const currentMoment = moment(date);

        const currentOffset = currentMoment.utcOffset();
        const nextOffset = currentMoment.clone().subtract(1, 'hour').utcOffset();
        const prevOffset = currentMoment.clone().add(1, 'hour').utcOffset();

        const isFallBack = (nextOffset > currentOffset) || (currentOffset > prevOffset);

        if (!isFallBack) {
            return null;
        }

        const localTimeString = currentMoment.format('YYYY-MM-DD HH:mm');
        const dstOffset = Math.max(currentOffset, prevOffset, nextOffset);
        const standardOffset = Math.min(currentOffset, prevOffset, nextOffset);

        let dstTime = moment.utc(localTimeString, true).subtract(dstOffset, 'minutes').toDate();
        let standardTime = moment.utc(localTimeString, true).subtract(standardOffset, 'minutes').toDate();

        // const isCurrentlyDST = currentOffset === dstOffset;
        // let targetTime: Date;
        // if (isCurrentlyDST) {
        //     targetTime = moment(standardTime).utcOffset(dstOffset, true).toDate();
        //     console.log("222222222222222222: " + convertDateToLocal(moment(standardTime)));
        // } else {
        //     targetTime = moment(dstTime).utcOffset(standardOffset, true).toDate();
        //     console.log("1111111111111111111: " + convertDateToLocal(moment(dstTime)));
        // }

        return {
            dstTime,
            standardTime
        };
    };

    return <>
        <StyledInputWrapper ref={wrapperRef}>
            <DateTimePickerGlobalStyles />
            <StyledInputLabel>
                {label}{' '}
                {required && !optional && <StyledRequired>*</StyledRequired>}
                {optional && !required && (
                    <StyledOptional><Lozenges variation="Optional Label" /></StyledOptional>
                )}
                {helpIcon && (
                    <IconButton
                        fileName="Icon-help"
                        size="small"
                        idIcon={`Icon-help-${id}`}
                        toolTipPlacement="right"
                        toolTipArrow={false}
                        disabled={false}
                        toolTipText={toolTipText}
                        {...rest}
                    />
                )}
                {systemIndicator && <HPHBadge value="s" color="Aqua-Green" />}
            </StyledInputLabel>

            <StyledCalendar
                style={{ width, ...(errorMessage || inputError) ? { borderColor: HPHRed } : {} }}
                className={disabled ? 'hph-disabled' : ''}
            >
                <input
                    id={id}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onFocus={() => showOnFocus && !disabled && setIsCalendarOpen(true)}
                    placeholder={placeholder}
                    disabled={disabled}
                    className="hph-inputtext"
                    {...rest}
                />

                <button
                    type="button"
                    className={`hph-button hph-button-icon-only ${disabled ? 'hph-disabled' : ''}`}
                    onClick={() => !disabled && setIsCalendarOpen(v => !v)}
                    disabled={disabled}
                    style={{
                        display: showOnFocus ? 'block' : 'none',
                        cursor: 'pointer',
                    }}
                >
                    <span className="hph-icon-calendar" />
                </button>

                {isCalendarOpen && (
                    <div className="hph-datepicker hph-component">
                        <div className="hph-datepicker-date" style={{ userSelect: 'none' }}
                            onWheel={(e) => {
                                if (disabled) return;
                                e.preventDefault();
                                if (e.deltaY < 0) prevMonth();
                                else if (e.deltaY > 0) nextMonth();
                            }}>
                            <div className="hph-datepicker-header">
                                <button className="hph-datepicker-prev" onClick={prevMonth}>
                                    <span className="hph-datepicker-prev-icon" />
                                </button>
                                <span>{monthNames[currentMonth]} {currentYear}</span>
                                <button className="hph-datepicker-next" onClick={nextMonth}>
                                    <span className="hph-datepicker-next-icon" />
                                </button>
                            </div>

                            <table>
                                <thead>
                                    <tr>
                                        <th>Su</th><th>Mo</th><th>Tu</th><th>We</th><th>Th</th><th>Fr</th><th>Sa</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...Array(Math.ceil(days.length / 7))].map((_, wi) => (
                                        <tr key={wi}>
                                            {days.slice(wi * 7, wi * 7 + 7).map((d, i) => (
                                                <td key={i}>
                                                    {d ? (
                                                        <span
                                                            className={
                                                                `${selectedDate?.getDate() === d && selectedDate?.getMonth() === currentMonth && selectedDate?.getFullYear() === currentYear ? 'hph-highlight' : ''} ${today.getDate() === d && today.getMonth() === currentMonth && today.getFullYear() === currentYear ? 'hph-today' : ''}
                                                                `
                                                            }
                                                            onClick={() => selectDay(d)}
                                                        >
                                                            {d}
                                                        </span>
                                                    ) : ''}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', borderLeft: '1px solid #ccc', marginLeft: '20px', height: '250px' }}>
                            {/* RADIO BUTTON */}
                            {(ambiguousDateTime.size > 0) &&
                                <div style={{ padding: '0 0 0 25px', margin: 0 }}>
                                    <HPHGroupRadioButton
                                        label=" "
                                        orientation={'vertical'}
                                        radioOptions={timeZoneSpecificationOptions}
                                        value={radioValue ?? ''}
                                        onChange={(e) => handleRadioChange(e)}
                                        checked={timeZoneSpecificationOptions?.find(item => item.key === radioValue)?.name}
                                    />
                                </div>
                            }
                            <div className="hph-timepicker" style={{ borderLeft: 'none', margin: 0, height: 'auto !important', minHeight: 'unset' }}>

                                {/* HOURS */}
                                <div className="hph-timepicker-unit">
                                    <button type="button" className="hph-timepicker-button" onMouseDown={(e) => changeTime('hours', 'up', e)} onTouchStart={(e) => changeTime('hours', 'up', e)} disabled={disabled}>
                                        <span className="hph-icon-angle-up" />
                                    </button>
                                    <input
                                        type="text"
                                        value={hours}
                                        onChange={(e) => handleTimeChange(e, 'hours')}
                                        onBlur={() => handleTimeBlur('hours')}
                                        onFocus={(e) => e.target.select()}
                                        onWheel={(e) => !disabled && changeTime('hours', e.deltaY < 0 ? 'up' : 'down', e)}
                                        maxLength={2}
                                        placeholder="HH"
                                        disabled={disabled}
                                        aria-label="Hours"
                                    />
                                    <button type="button" className="hph-timepicker-button" onMouseDown={(e) => changeTime('hours', 'down', e)} onTouchStart={(e) => changeTime('hours', 'down', e)} disabled={disabled}>
                                        <span className="hph-icon-angle-down" />
                                    </button>
                                </div>

                                <span className="hph-timepicker-unit hph-separator">:</span>

                                {/* MINUTES */}
                                <div className="hph-timepicker-unit">
                                    <button type="button" className="hph-timepicker-button" onMouseDown={(e) => changeTime('minutes', 'up', e)} onTouchStart={(e) => changeTime('minutes', 'up', e)} disabled={disabled}>
                                        <span className="hph-icon-angle-up" />
                                    </button>
                                    <input
                                        type="text"
                                        value={minutes}
                                        onChange={(e) => handleTimeChange(e, 'minutes')}
                                        onBlur={() => handleTimeBlur('minutes')}
                                        onFocus={(e) => e.target.select()}
                                        onWheel={(e) => !disabled && changeTime('minutes', e.deltaY < 0 ? 'up' : 'down', e)}
                                        maxLength={2}
                                        placeholder="MM"
                                        disabled={disabled}
                                        aria-label="Minutes"
                                    />
                                    <button type="button" className="hph-timepicker-button" onMouseDown={(e) => changeTime('minutes', 'down', e)} onTouchStart={(e) => changeTime('minutes', 'down', e)} disabled={disabled}>
                                        <span className="hph-icon-angle-down" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </StyledCalendar>

            {(errorMessage || inputError) && (
                <StyledInputErrorMsg>{errorMessage || inputError}</StyledInputErrorMsg>
            )}
        </StyledInputWrapper>
    </>
});

export { NbisDateTimePicker };